/**
 * THE REPORTING PERIOD VOCABULARY — v19 (proposal §2.5), mirrored from the API.
 *
 * The API resolves every dashboard and report window in the COUNTRY's calendar
 * (`apps/api/src/dashboards/dashboard/dashboard-periods.ts` and
 * `apps/api/src/dashboards/reports/report-periods.ts` — the same closed list, declared
 * twice there, once per module). This is the web's copy, held to BOTH by
 * `period-mirror.test.ts` (read as text — the `range-mirror.test.ts` idiom, because the
 * two apps are separate compilation units and a drift would otherwise be silent).
 *
 * ⚠ THE CHIPS SAY WHAT WAS ASKED; THE `window` ON THE WIRE SAYS WHAT WAS USED. Nothing
 * here computes a date. `custom` is a MODE (the two date inputs), not a chip; an
 * unrecognised `?period=` is passed through so the server's 422 names the vocabulary,
 * instead of the screen rendering the whole window under a URL claiming a filter.
 */

export const DASHBOARD_PERIODS = [
  'this_month',
  'last_month',
  'this_quarter',
  'last_quarter',
  'fytd',
  'custom',
] as const;
export type DashboardPeriod = (typeof DASHBOARD_PERIODS)[number];

export const DEFAULT_DASHBOARD_PERIOD: DashboardPeriod = 'this_month';

/** The quick chips, in the API's order; `custom` is the two date inputs. */
export const PERIOD_CHIPS: readonly { value: DashboardPeriod; label: string }[] = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'fytd', label: 'FYTD' },
];

/** What the server applied — `ResolvedPeriodDto` (dashboard) / the `window` root of a report. */
export interface PeriodWindowOnWire {
  readonly key: string;
  /** Inclusive 'YYYY-MM-DD'. */
  readonly from_date?: string;
  /** Inclusive 'YYYY-MM-DD'. */
  readonly to_date?: string;
  /** Reports spell the same two as `from` / `to` (inclusive dates). */
  readonly from?: string;
  readonly to?: string;
  readonly tz?: string;
}

/** The three URL parameters every period-taking screen reads, unvalidated on purpose. */
export interface PeriodQuery {
  readonly period: string | undefined;
  readonly from: string | undefined;
  readonly to: string | undefined;
}

export function readPeriodQuery(params: URLSearchParams): PeriodQuery {
  const period = params.get('period');
  const from = params.get('from');
  const to = params.get('to');
  return {
    period: period === null || period === '' ? undefined : period,
    from: from === null || from === '' ? undefined : from,
    to: to === null || to === '' ? undefined : to,
  };
}

/** "01 Aug 2026 – 31 Aug 2026" from date-only bounds; formatted in UTC so the stored day is the shown day. */
export function formatDateOnlyRange(from: string | undefined, to: string | undefined): string {
  const day = (value: string): string =>
    new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${value}T00:00:00.000Z`));
  if (from === undefined || to === undefined) return '';
  return `${day(from)} – ${day(to)}`;
}

/** The inclusive date bounds of a wire window, whichever spelling it used. */
export function windowBounds(window: PeriodWindowOnWire): { from?: string; to?: string } {
  return { from: window.from_date ?? window.from, to: window.to_date ?? window.to };
}
